import { isBadge } from "odyssey-shared";

/**
 * The staff badges the page holds for chat lines that carry no entity handle (ADR-112 decision 5.4): a line of Global,
 * Trade or Help and a whisper name the family that spoke, and the gateway's `speakerBadge` tells the page that family's
 * badge before the first such line and again when it changes, 0 taking it away. The page keeps family to badge for the
 * lines it draws, at most `MAX_FAMILY_BADGES`, the oldest told dropped first, and empties it on a reconnect. It holds only
 * what the line already told the page: that this family spoke.
 */

/**
 * The most families held.
 */
export const MAX_FAMILY_BADGES = 256;

/**
 * The family to badge map.
 */
export class FamilyBadges {
  private readonly held = new Map<string, number>();

  /**
   * How many families are held.
   *
   * @returns The count.
   */
  public get size(): number {
    return this.held.size;
  }

  /**
   * Takes a badge told for a family. A told family becomes the newest; one told 0, or a number that is no badge, is
   * forgotten, and the oldest family goes when a 257th would be held.
   *
   * @param familyName The family as the gateway holds it.
   * @param badge The badge, 0 to 15.
   */
  public tell(familyName: string, badge: number): void {
    const key = familyName.toLowerCase();
    this.held.delete(key);
    if (!isBadge(badge) || badge === 0) {
      return;
    }
    this.held.set(key, badge);
    if (this.held.size > MAX_FAMILY_BADGES) {
      const oldest = this.held.keys().next().value;
      if (oldest !== undefined) {
        this.held.delete(oldest);
      }
    }
  }

  /**
   * The badge held for a family.
   *
   * @param familyName The family, in any case.
   * @returns The badge, 0 for none.
   */
  public of(familyName: string): number {
    return this.held.get(familyName.toLowerCase()) ?? 0;
  }

  /**
   * Forgets every family, as a reconnect does.
   */
  public clear(): void {
    this.held.clear();
  }
}
